import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { enviroment } from "../enviroment";
import { useMsal } from "@azure/msal-react";
import { Organization } from "../core/models/entities/Organization";
import { useRepositoriesContext } from "./RepositoriesContext";
import {
  ACT_AS_STORAGE_KEY,
  clearActAsOrgIdStore,
  readStoredImpersonation,
  setActAsOrgIdStore,
} from "./actAsStore";

// The vendor user the admin is acting as. Kept minimal — just what the banner
// and the place-order attribution (OnBehalfOfUserId) need.
export interface ImpersonatedUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface OrganizationImpersonation {
  vendor?: Organization;
  organizationId?: number;
  user?: ImpersonatedUser;
  setVendor: (vendorId?: number) => void;
  setImpersonation: (vendorId?: number, user?: ImpersonatedUser) => void;
  isLoading: boolean;
}

const OrganizationImpersonationContext = createContext<
  OrganizationImpersonation | undefined
>(undefined);

// Storage/read/lazy-getter live in actAsStore.ts so ApiClient and the
// PreLoader prefetch wave can read the act-as scope without import cycles.

// Called on logout: a vendor/customer logging in later in the SAME tab must never
// inherit the admin's impersonation state — /profile/{orgId} is admin-only, so a
// stale restore would break their profile fetch on every reload.
function clearStoredImpersonation() {
  clearActAsOrgIdStore();
  try {
    sessionStorage.removeItem(ACT_AS_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing persisted, nothing to clear.
  }
}

interface Props {
  children: React.ReactNode;
}

const OrganizationImpersionationProvider = ({
  children,
}: Props): React.ReactElement => {
  const [vendorId, setVendorId] = useState<number | undefined>(
    () => readStoredImpersonation().vendorId
  );
  const [user, setUser] = useState<ImpersonatedUser | undefined>(
    () => readStoredImpersonation().user
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { accounts } = useMsal();
  const accountId = accounts[0]?.homeAccountId;

  const { organizationRepository } = useRepositoriesContext();
  const { data: vendor, refetch: refetchVendor } =
    organizationRepository.useVendor(vendorId);

  useEffect(() => {
    setIsLoading(true);
    refetchVendor().finally(() => {
      setIsLoading(false);
    });
  }, [vendorId]);

  // Restored state is bound to the admin who created it: if a DIFFERENT account
  // is signed in on this tab (shared machine, session expiry + new login), drop
  // the impersonation instead of restoring it for the wrong user. Validated in
  // an effect — accounts can be empty during MSAL's redirect handling at boot,
  // and a missing owner (never stored) must not discard a legitimate restore.
  useEffect(() => {
    if (vendorId === undefined) return;
    const owner = readStoredImpersonation().owner;
    if (owner && accountId && owner !== accountId) {
      setImpersonation(undefined, undefined);
    }
  }, [vendorId, accountId]);

  function setImpersonation(
    vendorId: number | undefined,
    user?: ImpersonatedUser
  ) {
    setVendorId(vendorId);
    setUser(vendorId === undefined ? undefined : user);
    // Synchronous, before any storage attempt: the module store must follow
    // the in-memory state even when sessionStorage is unavailable.
    setActAsOrgIdStore(vendorId);
    try {
      if (vendorId === undefined) {
        sessionStorage.removeItem(ACT_AS_STORAGE_KEY);
      } else {
        sessionStorage.setItem(
          ACT_AS_STORAGE_KEY,
          JSON.stringify({ vendorId, user, owner: accountId })
        );
      }
    } catch {
      // Storage unavailable (private mode quota etc.) — impersonation still
      // works for the current page lifetime, it just won't survive a reload.
    }
  }

  function setVendor(vendorId: number | undefined) {
    setImpersonation(vendorId, undefined);
  }

  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): stable setter identities
  // (thin wrappers that always call THIS render's implementation via a ref,
  // so nothing is ever stale) + one value object per state tuple. Flag off =
  // the literal object per render as before (hooks are called either way).
  const latestRef = useRef({ setVendor, setImpersonation });
  latestRef.current = { setVendor, setImpersonation };
  const stableSetVendor = useCallback(
    (id?: number) => latestRef.current.setVendor(id),
    []
  );
  const stableSetImpersonation = useCallback(
    (id?: number, u?: ImpersonatedUser) =>
      latestRef.current.setImpersonation(id, u),
    []
  );
  const memoValue = useMemo(
    () => ({
      vendor,
      organizationId: vendorId,
      user,
      setVendor: stableSetVendor,
      setImpersonation: stableSetImpersonation,
      isLoading,
    }),
    [vendor, vendorId, user, isLoading, stableSetVendor, stableSetImpersonation]
  );

  return (
    <OrganizationImpersonationContext.Provider
      value={
        enviroment.adminPerfNavMemoEnabled
          ? memoValue
          : {
              vendor,
              organizationId: vendorId,
              user,
              setVendor,
              setImpersonation,
              isLoading,
            }
      }
    >
      {children}
    </OrganizationImpersonationContext.Provider>
  );
};

function useOrganizationImpersonationContext(): OrganizationImpersonation {
  const context = useContext(OrganizationImpersonationContext);
  if (context === undefined) {
    throw new Error(
      "useOrganizationImpersonationContext must be used within a AdminImpersionationProvider"
    );
  }
  return context;
}

export {
  OrganizationImpersionationProvider,
  useOrganizationImpersonationContext,
  clearStoredImpersonation,
};
